/**
 * Premium API Tracer — captures rich telemetry from every axios request.
 *
 * Features:
 * - Full request/response lifecycle with timing breakdown
 * - Server-Timing header parsing (total, db, etc.)
 * - Response size estimation
 * - HAR export
 * - Sparkline data (rolling window of durations)
 * - Console replay integration
 * - Deduplication-safe (idempotent install)
 *
 * Only active when NODE_ENV !== 'production'.
 */

const MAX_ENTRIES = 500;
const SPARKLINE_WINDOW = 50;

class APITracer {
  constructor() {
    this.requests = [];
    this.errors = [];
    this.consoleErrors = [];
    this.listeners = new Set();
    this._id = 0;
    this._installed = false;
    this._startedAt = performance.now();
    this._sparkDurations = [];  // rolling window for sparkline
  }

  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  _emit() {
    this.listeners.forEach((fn) => {
      try { fn(); } catch {}
    });
  }

  /* ── Request lifecycle ── */

  onRequest(config) {
    const entry = {
      id: ++this._id,
      method: (config.method || 'GET').toUpperCase(),
      url: config.url,
      baseURL: config.baseURL || '',
      fullUrl: (config.baseURL || '') + (config.url || ''),
      startTime: performance.now(),
      startOffset: performance.now() - this._startedAt,
      timestamp: new Date(),
      status: 'pending',
      statusText: '',
      requestHeaders: this._sanitizeHeaders(config.headers),
      requestBody: this._safeClone(config.data),
      responseBody: null,
      responseHeaders: null,
      duration: null,
      size: null,
      traceId: null,
      serverTiming: null,
      serverTimingParsed: null,
      error: null,
      tags: [],                // ['slow', 'error', 'large', 'cached']
      _config: config,
    };
    config._traceEntryId = entry.id;
    this.requests.unshift(entry);
    if (this.requests.length > MAX_ENTRIES) this.requests.length = MAX_ENTRIES;
    this._emit();
    return config;
  }

  onResponse(response) {
    const entry = this._find(response.config?._traceEntryId);
    if (!entry) return response;

    const duration = performance.now() - entry.startTime;
    const hdrs = response.headers || {};

    entry.status = response.status;
    entry.statusText = response.statusText || '';
    entry.duration = duration;
    entry.responseHeaders = this._headersToObj(hdrs);
    entry.traceId = hdrs['x-trace-id'] || null;
    entry.serverTiming = hdrs['server-timing'] || null;
    entry.serverTimingParsed = this._parseServerTiming(entry.serverTiming);
    entry.size = this._estimateSize(response.data);
    entry.responseBody = this._safeClone(response.data);

    // Tags
    if (duration > 1000) entry.tags.push('slow');
    if (entry.size > 100_000) entry.tags.push('large');
    if (response.status === 304) entry.tags.push('cached');

    this._pushSparkline(duration);
    this._emit();
    return response;
  }

  onError(error) {
    const entryId = error.config?._traceEntryId;
    const entry = this._find(entryId);

    const errRecord = {
      id: ++this._id,
      timestamp: new Date(),
      message: error.message,
      code: error.code || null,
      status: error.response?.status || null,
      statusText: error.response?.statusText || '',
      url: error.config?.url || 'unknown',
      method: (error.config?.method || '').toUpperCase(),
      responseBody: this._safeClone(error.response?.data),
      responseHeaders: this._headersToObj(error.response?.headers),
      stack: error.stack,
      traceId: entry?.traceId || null,
    };
    this.errors.unshift(errRecord);
    if (this.errors.length > MAX_ENTRIES) this.errors.length = MAX_ENTRIES;

    if (entry) {
      entry.status = error.response?.status || 'ERR';
      entry.statusText = error.response?.statusText || error.code || '';
      entry.duration = performance.now() - entry.startTime;
      entry.error = error.message;
      entry.responseBody = this._safeClone(error.response?.data);
      entry.responseHeaders = this._headersToObj(error.response?.headers);
      entry.tags.push('error');
      this._pushSparkline(entry.duration);
    }

    this._emit();
    return Promise.reject(error);
  }

  /* ── Console capture ── */

  installConsoleCapture() {
    const origError = console.error;
    const origWarn = console.warn;
    const self = this;
    console.error = function (...args) {
      self.consoleErrors.unshift({
        id: ++self._id,
        level: 'error',
        timestamp: new Date(),
        args: args.map((a) => (typeof a === 'object' ? JSON.stringify(a, null, 2) : String(a))),
      });
      if (self.consoleErrors.length > 100) self.consoleErrors.length = 100;
      self._emit();
      origError.apply(console, args);
    };
    console.warn = function (...args) {
      self.consoleErrors.unshift({
        id: ++self._id,
        level: 'warn',
        timestamp: new Date(),
        args: args.map((a) => (typeof a === 'object' ? JSON.stringify(a, null, 2) : String(a))),
      });
      if (self.consoleErrors.length > 100) self.consoleErrors.length = 100;
      self._emit();
      origWarn.apply(console, args);
    };
  }

  /* ── Queries ── */

  clear() {
    this.requests = [];
    this.errors = [];
    this.consoleErrors = [];
    this._sparkDurations = [];
    this._emit();
  }

  getStats() {
    const done = this.requests.filter((r) => r.duration != null);
    if (!done.length) return { avg: 0, p50: 0, p95: 0, p99: 0, total: 0, errorRate: 0, pending: 0, totalSize: 0 };
    const ds = done.map((r) => r.duration).sort((a, b) => a - b);
    const errs = done.filter((r) => typeof r.status === 'number' && r.status >= 400);
    const pct = (p) => ds[Math.floor(ds.length * p)] || 0;
    return {
      avg: ds.reduce((a, b) => a + b, 0) / ds.length,
      p50: pct(0.5),
      p95: pct(0.95),
      p99: pct(0.99),
      total: done.length,
      errorRate: (errs.length / done.length) * 100,
      pending: this.requests.filter((r) => r.status === 'pending').length,
      totalSize: done.reduce((a, r) => a + (r.size || 0), 0),
    };
  }

  getSparklineData() {
    return [...this._sparkDurations];
  }

  getEndpointBreakdown() {
    const map = {};
    this.requests.filter((r) => r.duration != null).forEach((r) => {
      // Normalize URL: strip IDs (numeric segments)
      const normUrl = r.url?.replace(/\/\d+\//g, '/:id/').replace(/\/\d+$/, '/:id') || r.url;
      const key = `${r.method} ${normUrl}`;
      if (!map[key]) map[key] = { method: r.method, url: normUrl, calls: 0, durations: [], errors: 0, totalSize: 0 };
      map[key].calls++;
      map[key].durations.push(r.duration);
      map[key].totalSize += r.size || 0;
      if (typeof r.status === 'number' && r.status >= 400) map[key].errors++;
    });
    return Object.values(map)
      .map((ep) => {
        const sorted = ep.durations.sort((a, b) => a - b);
        return {
          ...ep,
          avg: sorted.reduce((a, b) => a + b, 0) / sorted.length,
          min: sorted[0],
          max: sorted[sorted.length - 1],
          p95: sorted[Math.floor(sorted.length * 0.95)] || 0,
        };
      })
      .sort((a, b) => b.avg - a.avg);
  }

  /* ── Export ── */

  exportHAR() {
    const entries = this.requests
      .filter((r) => r.duration != null)
      .map((r) => ({
        startedDateTime: r.timestamp.toISOString(),
        time: Math.round(r.duration),
        request: {
          method: r.method,
          url: r.fullUrl,
          headers: Object.entries(r.requestHeaders || {}).map(([n, v]) => ({ name: n, value: String(v) })),
          postData: r.requestBody ? { mimeType: 'application/json', text: typeof r.requestBody === 'string' ? r.requestBody : JSON.stringify(r.requestBody) } : undefined,
        },
        response: {
          status: r.status,
          statusText: r.statusText || '',
          headers: Object.entries(r.responseHeaders || {}).map(([n, v]) => ({ name: n, value: String(v) })),
          content: { size: r.size || 0, mimeType: 'application/json', text: typeof r.responseBody === 'string' ? r.responseBody : JSON.stringify(r.responseBody) },
        },
        timings: { send: 0, wait: Math.round(r.duration), receive: 0 },
      }));

    const har = {
      log: {
        version: '1.2',
        creator: { name: 'TradeSimul DevTools', version: '1.0' },
        entries,
      },
    };

    const blob = new Blob([JSON.stringify(har, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tradesimul-trace-${new Date().toISOString().slice(0, 19)}.har`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /* ── Internals ── */

  _find(id) { return this.requests.find((r) => r.id === id); }

  _estimateSize(data) {
    if (!data) return 0;
    try { return new Blob([typeof data === 'string' ? data : JSON.stringify(data)]).size; }
    catch { return 0; }
  }

  _safeClone(data) {
    if (data == null) return null;
    if (typeof data === 'string') return data.length > 50_000 ? data.slice(0, 50_000) + '\n…[truncated]' : data;
    try {
      const str = JSON.stringify(data);
      return str.length > 50_000 ? JSON.parse(str.slice(0, 50_000) + '"}') : data;
    } catch { return '<unserializable>'; }
  }

  _sanitizeHeaders(headers) {
    if (!headers) return {};
    const out = {};
    for (const [k, v] of Object.entries(headers)) {
      out[k] = k.toLowerCase() === 'authorization' ? '***' : v;
    }
    return out;
  }

  _headersToObj(headers) {
    if (!headers) return null;
    if (typeof headers === 'object' && !headers.forEach) return { ...headers };
    const out = {};
    try { headers.forEach((v, k) => { out[k] = v; }); } catch {}
    return out;
  }

  _parseServerTiming(header) {
    if (!header) return null;
    const result = {};
    header.split(',').forEach((part) => {
      const match = part.trim().match(/^(\w+);dur=([\d.]+)/);
      if (match) result[match[1]] = parseFloat(match[2]);
    });
    return Object.keys(result).length ? result : null;
  }

  _pushSparkline(duration) {
    this._sparkDurations.push(duration);
    if (this._sparkDurations.length > SPARKLINE_WINDOW) this._sparkDurations.shift();
  }
}

const tracer = new APITracer();
export default tracer;

export function installTracer(axiosInstance) {
  if (tracer._installed) return;
  tracer._installed = true;

  axiosInstance.interceptors.request.use(
    (c) => tracer.onRequest(c),
    (e) => Promise.reject(e)
  );
  axiosInstance.interceptors.response.use(
    (r) => tracer.onResponse(r),
    (e) => tracer.onError(e)
  );

  tracer.installConsoleCapture();
}
